import React from "react";
import { TID } from "@/constants/tids";
import logo from "@/assets/logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw,
  LogOut,
  Loader2,
  Sparkles,
  MessageSquare,
  Users,
  Sun,
  Moon,
  FileDown,
  Printer,
  BarChart3,
  KeyRound,
  ShieldCheck,
  LifeBuoy,
} from "lucide-react";


function TabButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

export default function AppHeader({
  status,
  theme,
  onToggleTheme,
  onSync,
  syncing,
  onDisconnect,
  onExportPdf,
  exporting,
  onPrint,
  printing,
  tab,
  setTab,
  onLogoHome,
  user,
  isAdmin,
  onLogout,
  onContactSupport,
}) {
  const connected = !!status.connected;
  const portalLabel = !connected
    ? "Not connected"
    : status.source === "csv"
    ? `CSV: ${status.portal_info?.filename || "upload"}`
    : status.portal_info?.portalId
    ? `Portal ${status.portal_info.portalId}`
    : "HubSpot";

  return (
    <header className="bg-card border-b border-border sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="header-logo-home"
            onClick={() => (onLogoHome ? onLogoHome() : setTab("connect"))}
            aria-label="Go to home"
            className="flex items-center gap-3 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 hover:opacity-80 transition-opacity"
          >
            <div className="w-9 h-9 flex items-center justify-center">
              <img src={logo} alt="Senior Sales Analyst logo" className="w-9 h-9 object-contain" />
            </div>
            <h1 className="font-semibold text-foreground leading-tight">SalesTap</h1>
          </button>
          <div>
            <p className="text-xs text-muted-foreground" data-testid={TID.connectionStatus}>
              {portalLabel}
              {connected && status.last_sync && ` · synced ${new Date(status.last_sync).toLocaleString()}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected && (
            <>
              <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10">
                Connected
              </Badge>
              <Button data-testid={TID.exportPdfBtn} onClick={onExportPdf} disabled={exporting} variant="outline" size="sm">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">PDF</span>
              </Button>
              <Button data-testid={TID.printBtn} onClick={onPrint} disabled={printing} variant="outline" size="sm">
                {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">Print</span>
              </Button>
              <Button data-testid={TID.syncBtn} onClick={onSync} disabled={syncing} variant="outline" size="sm">
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">Sync</span>
              </Button>
            </>
          )}
          <Button
            data-testid={TID.themeToggle}
            onClick={onToggleTheme}
            variant="outline"
            size="icon"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button
            data-testid="header-support-btn"
            onClick={onContactSupport}
            variant="outline"
            size="sm"
            title="Contact SalesTap support"
          >
            <LifeBuoy className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Support</span>
          </Button>
          {connected && (
            <Button
              data-testid={TID.disconnectBtn}
              onClick={onDisconnect}
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              title="Disconnect HubSpot (clears your CRM session)"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
          {user && (
            <div className="flex items-center gap-2 pl-2 ml-1 border-l border-border">
              <span className="text-xs text-muted-foreground hidden md:inline max-w-[180px] truncate" data-testid="account-email" title={user.email}>
                {user.email}
              </span>
              <Button
                data-testid="account-logout-btn"
                onClick={onLogout}
                variant="outline"
                size="sm"
              >
                <LogOut className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Log out</span>
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
        <TabButton testId={TID.tabConnect} active={tab === "connect"} onClick={() => setTab("connect")} icon={KeyRound}>
          Connect
        </TabButton>
        {connected && (
          <>
            <TabButton testId={TID.navAnalyticsMenu} active={tab === "analytics"} onClick={() => setTab("analytics")} icon={BarChart3}>
              Analytics
            </TabButton>

            <TabButton testId={TID.tabConnections} active={tab === "connections"} onClick={() => setTab("connections")} icon={Users}>
              Connections
            </TabButton>
            <TabButton testId={TID.tabChat} active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare}>
              Query Data
            </TabButton>
            <TabButton testId={TID.tabAnalyst} active={tab === "analyst"} onClick={() => setTab("analyst")} icon={Sparkles}>
              Sales Analysis
            </TabButton>
          </>
        )}
        {isAdmin && (
          <TabButton testId="tab-admin" active={tab === "admin"} onClick={() => setTab("admin")} icon={ShieldCheck}>
            Admin
          </TabButton>
        )}
      </div>
    </header>
  );
}
